import type { TFunction } from 'i18next';
import type { ComponentType } from 'react';
import { Folder } from 'lucide-react';
import { safeDistanceToNow } from '@/lib/safe-format';
import { ROOT_TEAM_ID } from '@scio3/shared-types';
import { formatDurationShort } from '@/features/playlists/utils/duration-helpers';
import { FEED_STATUS } from '@/features/playlists/hooks/useFeedEditor';
import { getFolderSubtitle } from './entity-types';

/**
 * Helpers for the compact "All view" row layout on the global-search page.
 *
 * These functions decide what each entity contributes to the three shared
 * regions of an All-view row:
 *   Line 1: [icon] [name] [badge…up to 2, then +N]
 *   Line 2: [subtitle — folder path + optional secondary meta]
 *   Right : [recency] (hides below xl, per the tags-column pattern)
 *
 * The single-type view (`?type=X`) continues to use the columnar renderers
 * built earlier; those carry the full detail set (tags, condition badges,
 * plan-lock warnings, etc.). All view is the discovery surface: 5 rows per
 * section, glance-and-click. Keep it lean — heavy detail lives one click away.
 */

// Entity → lucide icon lives in `entity-types.ts` as `ENTITY_ICONS` (a single
// map so the section header, the row leading slot, and the filter rail all
// render the same glyph per type — the previous duplicate map here disagreed
// with the filter rail's map and collapsed `schedules` / `operationalSchedules`
// onto the same icon, so both distinct backend buckets looked identical in
// the section header).

// ─── Badges ──────────────────────────────────────────────────────────────
// Line-1 inline pill(s). Priority-ordered — up to 2 render inline, remaining
// overflow into a `+N` chip whose tooltip reveals the rest (mirrors the
// existing tags column overflow pattern users already know from
// `renderTagsColumn`). Callers should render this list uniformly.
export interface AllViewBadge {
  /** Localized user-facing label. */
  label: string;
  /**
   * Semantic variant hint for the caller to pick a color. Kept as a string
   * (not an enum) so consumers can extend without touching this helper.
   * Common values: 'default', 'secondary', 'destructive', 'warning',
   * 'success', 'outline'.
   */
  variant: 'default' | 'secondary' | 'destructive' | 'warning' | 'success' | 'outline' | 'muted';
}

// Screens don't produce chips through this pipeline — `SearchPage.tsx`
// renders the native `<StatusBadge>` + `<ScreenConditionIndicators>`
// components directly (via `renderScreenBadges`) so the row's chip signature
// matches the native ScreensPage exactly (HDMI/wrong-time tinting,
// CirclePause icon, priority ordering, tooltips). The screens case in
// `getAllViewBadges` returns an empty array on purpose.

// Assets and engages don't produce chips through this pipeline either —
// `SearchPage.tsx` renders the native `<AssetTypeBadge>` + `<AssetStatusBadge>`
// components directly for those rows, matching what `AssetRow` /
// `EngageAssetRow` show. The screens + assets + engages cases in
// `getAllViewBadges` all return an empty array on purpose.

function teamTypeBadge(item: Record<string, unknown>, t: TFunction): AllViewBadge | null {
  const type = item.type as string | undefined;
  if (type === 'default') return { label: t('Default'), variant: 'secondary' };
  if (type === 'billing') return { label: t('Sub-Account'), variant: 'secondary' };
  if (type === 'adsPortal') return { label: t('Revue'), variant: 'secondary' };
  return null;
}

// Note: the previous `memberRoleBadge` helper was retired — members now
// render via the native `<RoleBadge>` component from
// `features/account-members/components/RoleBadge.tsx`, wired directly in
// `SearchPage.tsx`. Single source of truth for the role palette + label
// mapping, shared with AccountMembersPage.

/**
 * Build the priority-ordered badge list for an entity's line-1 slot. Caller
 * decides how many render inline vs collapse into `+N`.
 */
export function getAllViewBadges(
  entityType: string,
  item: Record<string, unknown>,
  t: TFunction,
): AllViewBadge[] {
  switch (entityType) {
    case 'screens':
      // Screens render via `renderScreenBadges` in `SearchPage.tsx` (native
      // `<StatusBadge>` + `<ScreenConditionIndicators>`), not through this
      // chip pipeline — see the header comment near the top of this file.
      return [];
    case 'assets':
    case 'engages':
      // Native `<AssetTypeBadge>` + `<AssetStatusBadge showDetail>`
      // rendered directly in `SearchPage` — see the header comment above.
      return [];
    case 'playlists': {
      const total = item.totalDuration as number | undefined;
      return total ? [{ label: formatDurationShort(total), variant: 'secondary' }] : [];
    }
    case 'folders': {
      const label = getFolderSubtitle(item, t);
      return label ? [{ label, variant: 'secondary' }] : [];
    }
    case 'feeds': {
      const badges: AllViewBadge[] = [];
      if (item.status === FEED_STATUS.UNPUBLISH) {
        badges.push({ label: t('Draft'), variant: 'secondary' });
      }
      if (item.featured === true) {
        badges.push({ label: t('Featured'), variant: 'default' });
      }
      return badges;
    }
    case 'teams': {
      const badge = teamTypeBadge(item, t);
      return badge ? [badge] : [];
    }
    case 'members':
      // Native `<RoleBadge>` rendered directly in `SearchPage` — see the
      // header comment above (same reuse pattern as screens + assets).
      return [];
    default:
      return [];
  }
}

// ─── Subtitle ────────────────────────────────────────────────────────────
// Line-2 label. Truncatable, may include a leading icon. Feeds return null
// (they're global — no folder context worth surfacing on a discovery row).
export interface AllViewSubtitle {
  /** Optional leading icon (folder for path-based rows, mail for members, etc.). */
  icon: ComponentType<{ className?: string }> | null;
  text: string;
}

/**
 * Build a hierarchical folder path label from `parentGroupInfo`. Mirrors what
 * LocationCell renders on the right column of the single-type view, but as a
 * plain string for inline use in the subtitle. `parentGroupInfo` = null →
 * treat as root ("Home").
 */
function folderPathLabel(item: Record<string, unknown>, t: TFunction): string {
  const parent = item.parentGroupInfo as { name?: string; path?: string | null } | undefined;
  if (!parent?.name) return t('Home');
  const above = parent.path && parent.path !== '/' ? parent.path : '';
  const joined = `${above}/${parent.name}`.replace(/^\//, '');
  return joined || t('Home');
}

export function getAllViewSubtitle(
  entityType: string,
  item: Record<string, unknown>,
  t: TFunction,
): AllViewSubtitle | null {
  switch (entityType) {
    case 'screens': {
      // Screens subtitle carries three signals — folder path, OS, and the
      // content-assignment label ("Asset: X" / "Playlist: X" / "Schedule:
      // X" / "Stop Playing" / "No content assigned"). Mirrors the format
      // `ContentAssignmentDropdown.tsx:56-67` uses. Putting content in
      // the subtitle line (instead of a separate Col 2) keeps it visible
      // on ALL viewports rather than only wide ones — critical on
      // mobile-narrow screens where operators troubleshoot devices.
      // The trailing content part truncates first when space runs low.
      const path = folderPathLabel(item, t);
      const os = item.os as string | undefined;
      const parts = [path];
      if (os) parts.push(capitalize(os));
      parts.push(screenContentLabel(item, t));
      return { icon: Folder, text: parts.join(' · ') };
    }
    case 'assets':
    case 'engages':
    case 'playlists':
    case 'schedules':
    case 'operationalSchedules':
      return { icon: Folder, text: folderPathLabel(item, t) };
    case 'folders': {
      // Folders' subtitle is the PARENT folder path (where this folder lives),
      // not the folder's own path. `item.path` from the folder wire IS the
      // parent path (folder's leaf is `item.name`).
      const parentPath = item.path as string | undefined;
      const trimmed = parentPath && parentPath !== '/' ? parentPath.replace(/^\//, '') : '';
      return { icon: Folder, text: trimmed || t('Home') };
    }
    case 'feeds':
      // Feeds are global — no folder context to surface.
      return null;
    case 'teams': {
      // Combine member count + description on the subtitle line so both
      // stay visible on narrow viewports (Col 2 hides below md — if we
      // kept description there, small screens would lose it entirely).
      // Description is typically short and pithy; long ones truncate
      // with ellipsis, same as any other subtitle text.
      const count = typeof item.memberCount === 'number' ? (item.memberCount as number) : null;
      const description = item.description as string | undefined;
      const parts: string[] = [];
      if (count != null) parts.push(t('{{count}} members', { count }));
      if (description) parts.push(description);
      if (parts.length === 0) return null;
      return { icon: null, text: parts.join(' · ') };
    }
    case 'members': {
      // Members: email + team name joined on the same subtitle line.
      // ROOT_TEAM_ID renders as the localized "Default Team" label.
      // Frees up the right column to carry the "Updated X ago" recency
      // that every other entity shows, so members no longer look empty
      // on the far-right of the row.
      const email = item.email as string | undefined;
      const teamId = item.teamId as string | undefined;
      const teamName = item.teamName as string | undefined;
      const teamLabel = teamId === ROOT_TEAM_ID ? t('Default Team') : (teamName ?? '');
      const parts = [email, teamLabel].filter(Boolean);
      if (parts.length === 0) return null;
      return { icon: null, text: parts.join(' · ') };
    }
    default:
      return null;
  }
}

// ─── Recency ─────────────────────────────────────────────────────────────
// Right-column string. Different verb per entity — Screens use "Last online X ago"
// (real device liveness), everything else uses "Updated X ago" (content
// authoring signal). Teams + Members have no meaningful recency for a search
// discovery surface (a team hasn't "been active" — it just exists).
//
// Shared source-of-truth for what freshness field each entity surfaces.
// `getAllViewRecency` (below, for display) and `sortSearchItems` (in
// `./sort.ts`, for "Last modified" sort) MUST both go through this helper,
// otherwise the row's visible date and its sort position drift apart —
// e.g. Screens historically showed `lastHeartBeat` in the recency column
// but sorted by `lastUpdatedDate`, so a screen last seen 7 years ago whose
// content was reassigned yesterday would render "Last online 7 years ago" yet
// jump to the top of the sort. That's the regression this helper prevents.
export function getRecencyTimestamp(
  entityType: string,
  item: Record<string, unknown>,
): string | undefined {
  // Screens surface real device liveness (`lastHeartBeat`) — that's what
  // the user sees ("Last online X ago") and therefore what sort must key on.
  if (entityType === 'screens') {
    return item.lastHeartBeat as string | undefined;
  }
  // Feeds intentionally show no recency (curated templates), but the sort
  // still needs a stable key — fall back to `lastUpdatedDate` so feeds
  // don't randomly reorder.
  return item.lastUpdatedDate as string | undefined;
}
export function getAllViewRecency(
  entityType: string,
  item: Record<string, unknown>,
  t: TFunction,
): string | null {
  switch (entityType) {
    case 'screens': {
      const lastHeartBeat = getRecencyTimestamp('screens', item);
      if (!lastHeartBeat) return null;
      return t('Last online {{time}}', {
        time: safeDistanceToNow(lastHeartBeat, { addSuffix: true }),
      });
    }
    case 'feeds':
      // Feeds are OptiSigns-curated templates — the freshness signal
      // ("Updated 6 months ago") isn't actionable and just adds noise on
      // a discovery surface where users are picking a template to
      // publish. Identity comes from the thumbnail + name + Draft/
      // Featured pill; that's enough. Right column stays empty.
      return null;
    case 'assets':
    case 'engages':
    case 'playlists':
    case 'schedules':
    case 'operationalSchedules':
    case 'folders': {
      const last = item.lastUpdatedDate as string | undefined;
      if (!last) return null;
      return t('Updated {{time}}', {
        time: safeDistanceToNow(last, { addSuffix: true }),
      });
    }
    case 'teams': {
      // Teams weren't especially "active" over time, but `lastUpdatedDate`
      // tracks name/description/settings/member-list edits — the best
      // available freshness signal without adding a dedicated activity
      // aggregation. Same "Updated X ago" verb as content-shaped entities
      // so the right column stays visually consistent across the mixed
      // All-view sections.
      const last = item.lastUpdatedDate as string | undefined;
      if (!last) return null;
      return t('Updated {{time}}', {
        time: safeDistanceToNow(last, { addSuffix: true }),
      });
    }
    case 'members': {
      // Team name moved into the subtitle line (alongside email) so the
      // right column can carry "Updated X ago" — same freshness signal
      // every other entity surfaces. Falls back to null when the pairing
      // has no date stamped (rare — `UserMember.date` defaults on write).
      const last = item.lastUpdatedDate as string | undefined;
      if (!last) return null;
      return t('Updated {{time}}', {
        time: safeDistanceToNow(last, { addSuffix: true }),
      });
    }
    default:
      return null;
  }
}

/**
 * Bare relative time for the TABLE layout's recency column — "7 years ago",
 * no verb prefix. The column HEADER already says "Last online" / "Last
 * updated" (see `search-columns.ts`), so repeating the verb in every cell
 * is noise. `getAllViewRecency` above keeps the full labelled string for
 * surfaces with no column header (the top-nav dropdown).
 *
 * Keys off the same `getRecencyTimestamp` as sort, so a row's visible date
 * and its sort position can't drift apart. Feeds return null there by
 * design; here they return null too and the cell renders empty.
 */
export function getRecencyShort(entityType: string, item: Record<string, unknown>): string | null {
  // Feeds show no recency at all — curated templates, the date isn't
  // actionable (same rule as `getAllViewRecency`).
  if (entityType === 'feeds') return null;
  const ts = getRecencyTimestamp(entityType, item);
  if (!ts) return null;
  return safeDistanceToNow(ts, { addSuffix: true });
}

// ─── Small util ──────────────────────────────────────────────────────────
function capitalize(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Screen content-assignment label — same three branches
 * `ContentAssignmentDropdown.tsx:56-67` picks between:
 *   1. Something assigned → "Asset: filename" / "Playlist: name" / "Schedule: name"
 *   2. Stopped with recent history → "Stop Playing"
 *   3. Otherwise → "No content assigned"
 */
export function screenContentLabel(item: Record<string, unknown>, t: TFunction): string {
  const assign = item.assignItem as { key?: string; value?: string; type?: string } | undefined;
  const currentType = item.currentType as string | undefined;
  const hasContent = !!currentType && currentType !== 'none' && !!assign;
  const isStopped = currentType === 'none';
  const recentAssignments = (item.recentAssignments as unknown[] | undefined) ?? [];
  const contentTypeLabel = capitalize(assign?.type ?? currentType ?? '');
  if (hasContent) {
    return assign?.value ? `${contentTypeLabel}: ${assign.value}` : contentTypeLabel;
  }
  if (isStopped && recentAssignments.length > 0) return t('Stop Playing');
  return t('No content assigned');
}
